import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  AlertOctagon,
  AlertTriangle,
  Calendar,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  Cpu,
  FileText,
  Filter,
  HardDrive,
  Loader2,
  MemoryStick,
  Network,
  RefreshCw,
  Search,
  Server,
  XCircle,
} from "lucide-react";
import React from "react";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/Card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/Dialog";
import { Input } from "../../components/ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/Select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table";
import { fetchWithAuth } from "../../hooks/useAuth";
import { formatDate } from "../../utils/format";

interface ServiceStatus {
  name: string;
  status: "operational" | "degraded" | "down";
  latency?: number;
  lastCheck: string;
  message?: string;
}

interface SystemMetrics {
  cpu: number;
  memory: {
    total: number;
    used: number;
    free: number;
  };
  disk: {
    total: number;
    used: number;
    free: number;
  };
  uptime: number;
  activeConnections: number;
}

interface ErrorLog {
  id: number;
  timestamp: string;
  level: string;
  message: string;
  stack?: string;
  details?: Record<string, any>;
}

interface LogsResponse {
  logs: ErrorLog[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

interface LogFilters {
  level?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
  page: number;
  limit: number;
}

const statusIcons = {
  operational: <CheckCircle className="w-4 h-4" />,
  degraded: <AlertTriangle className="w-4 h-4" />,
  down: <XCircle className="w-4 h-4" />,
};

const statusColors = {
  operational: "bg-green-100 text-green-800 ring-1 ring-green-200",
  degraded: "bg-yellow-100 text-yellow-800 ring-1 ring-yellow-200",
  down: "bg-red-100 text-red-800 ring-1 ring-red-200",
};

const logLevelColors = {
  error: "bg-red-100 text-red-800 ring-1 ring-red-200",
  warn: "bg-yellow-100 text-yellow-800 ring-1 ring-yellow-200",
  info: "bg-blue-100 text-blue-800 ring-1 ring-blue-200",
};

export function MonitoringPage() {
  const [selectedLog, setSelectedLog] = React.useState<ErrorLog | null>(null);

  // Fetch services status
  const { data: services, refetch: refetchServices } = useQuery({
    queryKey: ["services-status"],
    queryFn: async () => {
      const response = await fetchWithAuth("/api/admin/monitoring/services");
      if (!response.ok) throw new Error("Eroare la verificarea serviciilor");
      return response.json() as Promise<ServiceStatus[]>;
    },
    enabled: true, // Initial fetch
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchInterval: false, // Disable auto-refresh
  });

  // Fetch system metrics
  const { data: metrics, refetch: refetchMetrics } = useQuery({
    queryKey: ["system-metrics"],
    queryFn: async () => {
      const response = await fetchWithAuth("/api/admin/monitoring/metrics");
      if (!response.ok) throw new Error("Eroare la obținerea metricilor");
      return response.json() as Promise<SystemMetrics>;
    },
    enabled: true, // Initial fetch
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchInterval: false, // Disable auto-refresh
  });

  const [filters, setFilters] = React.useState<LogFilters>({
    page: 1,
    limit: 10,
  });

  // Fetch error logs - manual refresh only
  const {
    data: logsData,
    refetch: refetchLogs,
    isLoading: isLoadingLogs,
  } = useQuery({
    queryKey: ["error-logs", filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.level) params.append("level", filters.level);
      if (filters.search) params.append("search", filters.search);
      if (filters.startDate) params.append("startDate", filters.startDate);
      if (filters.endDate) params.append("endDate", filters.endDate);
      params.append("page", String(filters.page));
      params.append("limit", String(filters.limit));

      const response = await fetchWithAuth(
        `/api/admin/monitoring/logs?${params}`
      );
      if (!response.ok) throw new Error("Eroare la obținerea logurilor");
      return response.json() as Promise<LogsResponse>;
    },
    enabled: true, // Initial fetch
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchInterval: false, // Disable auto-refresh
  });

  const formatBytes = (bytes: number | null | undefined): string => {
    if (bytes === null || bytes === undefined || bytes === 0) return "0 B";
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const value = bytes / Math.pow(1024, i);
    if (isNaN(value)) return "0 B";
    return `${Math.round(value)} ${sizes[i]}`;
  };

  const formatUptime = (seconds: number): string => {
    const days = Math.floor(seconds / (24 * 60 * 60));
    const hours = Math.floor((seconds % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((seconds % (60 * 60)) / 60);
    return `${days}d ${hours}h ${minutes}m`;
  };

  return (
    <div className="p-6 space-y-8 bg-background min-h-screen">
      {/* Header */}
      <Card className="flex justify-between items-center ">
        <div className="flex items-center gap-2">
          <Activity className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              Monitorizare Sistem
            </h1>
            <p className="text-sm text-muted-foreground">
              Monitorizare servicii și performanță
            </p>
          </div>
        </div>
        <Button
          className="flex items-center gap-2"
          onClick={async () => {
            await Promise.all([refetchServices(), refetchMetrics()]);
          }}
        >
          <RefreshCw className="w-4 h-4" />
          Reîmprospătează
        </Button>
      </Card>

      {/* Services Status */}
      <Card className="">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="w-5 h-5" />
            Status Servicii
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services?.map((service) => (
              <Card key={service.name} className="">
                <CardContent className="pt-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="font-medium text-foreground">
                      {service.name}
                    </div>
                    <Badge className={statusColors[service.status]}>
                      <div className="flex items-center gap-1">
                        {statusIcons[service.status]}
                        {service.status === "operational"
                          ? "Operațional"
                          : service.status === "degraded"
                          ? "Performanță Redusă"
                          : "Indisponibil"}
                      </div>
                    </Badge>
                  </div>
                  {service.latency && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                      <Network className="w-4 h-4" />
                      Latență: {service.latency}ms
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="w-4 h-4" />
                    Ultima verificare: {formatDate(service.lastCheck)}
                  </div>
                  {service.message && (
                    <div className="mt-2 p-2 rounded-md bg-gray-50 text-sm text-muted-foreground">
                      {service.message}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* System Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* CPU & Memory */}
        <Card className="">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Cpu className="w-5 h-5" />
              CPU & Memorie
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div>
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-2">
                  <Cpu className="w-4 h-4" />
                  Utilizare CPU
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-2xl font-bold text-foreground w-20">
                    {typeof metrics?.cpu === "number" && !isNaN(metrics.cpu)
                      ? `${Math.round(metrics.cpu)}%`
                      : "-"}
                  </div>
                  <div className="flex-1">
                    <div className="w-full bg-gray-100 rounded-full h-2.5">
                      <div
                        className={`h-2.5 rounded-full transition-all duration-500 ${
                          (metrics?.cpu || 0) > 80
                            ? "bg-red-500"
                            : (metrics?.cpu || 0) > 50
                            ? "bg-yellow-500"
                            : "bg-green-500"
                        }`}
                        style={{ width: `${metrics?.cpu || 0}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-2">
                  <MemoryStick className="w-4 h-4" />
                  Memorie
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-2xl font-bold text-foreground w-20">
                    {metrics?.memory?.total &&
                    metrics.memory.used &&
                    !isNaN(metrics.memory.total) &&
                    !isNaN(metrics.memory.used)
                      ? `${Math.round(
                          (metrics.memory.used / metrics.memory.total) * 100
                        )}%`
                      : "-"}
                  </div>
                  <div className="flex-1">
                    <div className="w-full bg-gray-100 rounded-full h-2.5">
                      <div
                        className={`h-2.5 rounded-full transition-all duration-500 ${
                          metrics?.memory
                            ? metrics.memory.used / metrics.memory.total > 0.8
                              ? "bg-red-500"
                              : metrics.memory.used / metrics.memory.total > 0.5
                              ? "bg-yellow-500"
                              : "bg-green-500"
                            : "bg-gray-200"
                        }`}
                        style={{
                          width: `${
                            metrics?.memory
                              ? (metrics.memory.used / metrics.memory.total) *
                                100
                              : 0
                          }%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-2 text-sm text-muted-foreground">
                  {metrics?.memory &&
                    `${formatBytes(
                      metrics.memory.used
                    )} folosit din ${formatBytes(metrics.memory.total)}`}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Disk & Network */}
        <Card className="">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <HardDrive className="w-5 h-5" />
              Disk & Rețea
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div>
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-2">
                  <HardDrive className="w-4 h-4" />
                  Spațiu Disk
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-2xl font-bold text-foreground w-20">
                    {metrics?.disk?.total &&
                    metrics.disk.used &&
                    !isNaN(metrics.disk.total) &&
                    !isNaN(metrics.disk.used)
                      ? `${Math.round(
                          (metrics.disk.used / metrics.disk.total) * 100
                        )}%`
                      : "-"}
                  </div>
                  <div className="flex-1">
                    <div className="w-full bg-gray-100 rounded-full h-2.5">
                      <div
                        className={`h-2.5 rounded-full transition-all duration-500 ${
                          metrics?.disk
                            ? metrics.disk.used / metrics.disk.total > 0.8
                              ? "bg-red-500"
                              : metrics.disk.used / metrics.disk.total > 0.5
                              ? "bg-yellow-500"
                              : "bg-green-500"
                            : "bg-gray-200"
                        }`}
                        style={{
                          width: `${
                            metrics?.disk
                              ? (metrics.disk.used / metrics.disk.total) * 100
                              : 0
                          }%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-2 text-sm text-muted-foreground">
                  {metrics?.disk &&
                    `${formatBytes(
                      metrics.disk.used
                    )} folosit din ${formatBytes(metrics.disk.total)}`}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-2">
                    <Network className="w-4 h-4" />
                    Conexiuni Active
                  </div>
                  <div className="text-2xl font-bold text-foreground">
                    {metrics?.activeConnections || 0}
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-2">
                    <Clock className="w-4 h-4" />
                    Uptime
                  </div>
                  <div className="text-2xl font-bold text-foreground">
                    {metrics?.uptime && formatUptime(metrics.uptime)}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Error Logs */}
      <Card className="">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <AlertOctagon className="w-5 h-5" />
              Loguri de Eroare
            </CardTitle>
            <Button
              onClick={() => refetchLogs()}
              disabled={isLoadingLogs}
              className="flex items-center gap-2"
            >
              {isLoadingLogs ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Se încarcă...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" />
                  Actualizează
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <Card className="mb-6 p-4 border border-border">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  Nivel
                </label>
                <Select
                  value={filters.level || "all"}
                  onValueChange={(value) =>
                    setFilters((f) => ({
                      ...f,
                      level: value === "all" ? undefined : value,
                      page: 1,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Toate nivelurile" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toate nivelurile</SelectItem>
                    <SelectItem value="info">Info</SelectItem>
                    <SelectItem value="warn">Warning</SelectItem>
                    <SelectItem value="error">Error</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                  <Search className="w-4 h-4" />
                  Caută
                </label>
                <Input
                  placeholder="Caută în loguri..."
                  value={filters.search || ""}
                  onChange={(e) =>
                    setFilters((f) => ({
                      ...f,
                      search: e.target.value || undefined,
                      page: 1,
                    }))
                  }
                />
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  De la
                </label>
                <Input
                  type="date"
                  value={filters.startDate || ""}
                  onChange={(e) =>
                    setFilters((f) => ({
                      ...f,
                      startDate: e.target.value || undefined,
                      page: 1,
                    }))
                  }
                />
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Până la
                </label>
                <Input
                  type="date"
                  value={filters.endDate || ""}
                  onChange={(e) =>
                    setFilters((f) => ({
                      ...f,
                      endDate: e.target.value || undefined,
                      page: 1,
                    }))
                  }
                />
              </div>
            </div>
          </Card>

          <div className="rounded-md border border-border">
            <Table>
              <TableHeader>
                <TableRow className="">
                  <TableHead className="font-semibold">Timestamp</TableHead>
                  <TableHead className="font-semibold">Nivel</TableHead>
                  <TableHead className="font-semibold">Mesaj</TableHead>
                  <TableHead className="font-semibold">Detalii</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!logsData?.logs.length ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-10">
                      {isLoadingLogs ? (
                        <div className="flex flex-col items-center gap-2">
                          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                          <p className="text-muted-foreground">Se încarcă...</p>
                        </div>
                      ) : (
                        <div className="text-muted-foreground">
                          Nu există loguri de eroare
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  logsData.logs.map((log) => (
                    <TableRow key={log.id} className="">
                      <TableCell>{formatDate(log.timestamp)}</TableCell>
                      <TableCell>
                        <Badge
                          className={
                            logLevelColors[
                              log.level as keyof typeof logLevelColors
                            ]
                          }
                        >
                          {log.level.toUpperCase()}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-md truncate">
                        {log.message}
                      </TableCell>
                      <TableCell>
                        {log.stack && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-1"
                            onClick={() => setSelectedLog(log)}
                          >
                            <FileText className="w-4 h-4" />
                            Stack Trace
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {logsData && logsData.pagination.totalPages > 1 && (
            <div className="mt-4 flex justify-between items-center">
              <div className="text-sm text-muted-foreground">
                {`Afișare ${
                  (filters.page - 1) * filters.limit + 1
                } - ${Math.min(
                  filters.page * filters.limit,
                  logsData.pagination.total
                )} din ${logsData.pagination.total} loguri`}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1"
                  disabled={filters.page === 1 || isLoadingLogs}
                  onClick={() => {
                    setFilters((f) => ({ ...f, page: f.page - 1 }));
                    refetchLogs();
                  }}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1"
                  disabled={
                    filters.page === logsData.pagination.totalPages ||
                    isLoadingLogs
                  }
                  onClick={() => {
                    setFilters((f) => ({ ...f, page: f.page + 1 }));
                    refetchLogs();
                  }}
                >
                  Următor
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stack Trace Dialog */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Stack Trace
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <pre className="text-xs bg-gray-50 p-4 rounded-lg overflow-auto max-h-[60vh] font-mono">
              {selectedLog?.stack}
            </pre>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
